import { AxiosError } from 'axios'
import { differenceInDays, isAfter, isBefore, max, min } from 'date-fns'
import startCase from 'lodash/startCase'
import { formatDate } from 'lib/date-time'
import {
  UTILITY_BILL_CHARGE_TYPES,
  UTILITY_BILL_STATUSES,
  UTILITY_CHARGEBACK_STATUSES
} from '@homevest/utils/utilities'
import { UtilityBill, UtilityBillStatusDisplay } from './types'

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/

export const LIKELY_CANDIDATE_LOOKBACK_MONTHS = 6

const roundToCurrency = (amount: number) => Math.round(amount * 100) / 100

export const parseBillDate = (date?: string | null) => {
  if (!date) {
    return null
  }

  return DATE_ONLY_REGEX.test(date) ? new Date(`${date}T12:00:00`) : new Date(date)
}

export const formatDisplayDate = (date?: string | null) => {
  if (!date) {
    return 'N/A'
  }

  if (DATE_ONLY_REGEX.test(date)) {
    return date
  }

  const parsedDate = parseBillDate(date)

  return parsedDate ? formatDate(parsedDate, 'YYYY-MM-DD') : 'N/A'
}

export const getUtilityTypeLabel = (name?: string | null) => startCase(name ?? '')

export const getDefaultNote = (bill: UtilityBill) =>
  `Utility chargeback for ${getUtilityTypeLabel(
    bill.utility_type.name
  ).toLowerCase()} (${formatDisplayDate(bill.service_start_date)} - ${formatDisplayDate(
    bill.service_end_date
  )})`

export const getBuildiumBillUrl = (bankAccountId: number, buildiumId: number) =>
  `https://upandup.managebuilding.com/manager/app/banking/bank-account/${bankAccountId}/register?journalId=${buildiumId}`

export const buildRejectedChargebackWarning = (finalizedAt?: string | null) =>
  `A previous chargeback for this bill was rejected on ${formatDisplayDate(
    finalizedAt
  )}. Proceeding will re-approve it.`

export const getBillPaymentStatusLabel = (status?: string | null) => startCase(status ?? 'unknown')

export const getBillPaymentStatusBadgeColor = (status?: string | null) => {
  switch (status) {
    case UTILITY_BILL_STATUSES.PAID:
      return undefined
    case UTILITY_BILL_STATUSES.PENDING:
      return 'Blue'
    default:
      return 'Yellow'
  }
}

export const buildNoAutomaticChargebackWarning = (
  status: string | null | undefined,
  chargeType: string
) => {
  if (
    status !== UTILITY_BILL_STATUSES.PAID &&
    chargeType === UTILITY_BILL_CHARGE_TYPES.SERVICE_CHARGE
  ) {
    return 'Bill has not been paid yet. If this bill is eligible for automatic chargeback creation, it will be created when the bill is paid.'
  }

  if (chargeType !== UTILITY_BILL_CHARGE_TYPES.SERVICE_CHARGE) {
    return `Chargeback was not automatically generated because this bill is a ${startCase(
      chargeType
    ).toLowerCase()}, not a service charge. Please verify this chargeback is correct.`
  }

  return 'Chargeback was not automatically generated for this bill. This is unusual — please verify this chargeback is correct.'
}

export const getChargebackForRental = (bill: UtilityBill, rentalId: string) =>
  bill.utility_chargebacks.find((chargeback) => chargeback.rental_id === rentalId)

export const getInitialWarningForSelectedBill = (bill: UtilityBill, rentalId: string) => {
  const chargeback = getChargebackForRental(bill, rentalId)

  if (chargeback?.status === UTILITY_CHARGEBACK_STATUSES.REJECTED) {
    return buildRejectedChargebackWarning(chargeback.finalized_at)
  }

  if (!chargeback) {
    return buildNoAutomaticChargebackWarning(bill.status, bill.charge_type)
  }

  return undefined
}

export const getStatusDisplay = (bill: UtilityBill, rentalId: string): UtilityBillStatusDisplay => {
  const chargeback = getChargebackForRental(bill, rentalId)

  if (!chargeback) {
    return {
      colorScheme: 'green',
      description: 'No chargeback',
      isSelectable: true
    }
  }

  if (chargeback.status === UTILITY_CHARGEBACK_STATUSES.APPROVED) {
    return {
      colorScheme: 'gray',
      description: 'Already charged back',
      isSelectable: false
    }
  }

  if (chargeback.status === UTILITY_CHARGEBACK_STATUSES.REJECTED) {
    return {
      colorScheme: 'yellow',
      description: `Rejected on ${formatDisplayDate(chargeback.finalized_at)}`,
      isSelectable: true
    }
  }

  return {
    colorScheme: 'blue',
    description: 'Pending chargeback exists',
    isSelectable: true
  }
}

export const getStatusBadgeColor = (colorScheme: UtilityBillStatusDisplay['colorScheme']) => {
  switch (colorScheme) {
    case 'green':
      return 'Green'
    case 'yellow':
      return 'Yellow'
    case 'blue':
      return 'Blue'
    case 'gray':
    default:
      return 'Gray'
  }
}

export const getProratedChargebackAmount = ({
  billAmount,
  finalLiabilityDate,
  occupancyDate,
  serviceEndDate,
  serviceStartDate
}: {
  billAmount: number
  finalLiabilityDate?: string | null
  occupancyDate: string
  serviceEndDate?: string | null
  serviceStartDate?: string | null
}) => {
  const parsedOccupancyDate = parseBillDate(occupancyDate)
  const parsedFinalLiabilityDate = parseBillDate(finalLiabilityDate)
  const parsedServiceStartDate = parseBillDate(serviceStartDate)
  const parsedServiceEndDate = parseBillDate(serviceEndDate)

  if (
    !parsedOccupancyDate ||
    !parsedServiceStartDate ||
    !parsedServiceEndDate ||
    !Number.isFinite(billAmount)
  ) {
    return roundToCurrency(billAmount)
  }

  const finalLiabilityBeforeServiceStart =
    !!parsedFinalLiabilityDate && isBefore(parsedFinalLiabilityDate, parsedServiceStartDate)
  const occupancyAfterServiceEnd = isAfter(parsedOccupancyDate, parsedServiceEndDate)

  if (finalLiabilityBeforeServiceStart || occupancyAfterServiceEnd) {
    return 0
  }

  const finalLiabilityBeforeServiceEnd =
    !!parsedFinalLiabilityDate && isBefore(parsedFinalLiabilityDate, parsedServiceEndDate)
  const occupancyAfterServiceStart = isAfter(parsedOccupancyDate, parsedServiceStartDate)

  if (!finalLiabilityBeforeServiceEnd && !occupancyAfterServiceStart) {
    return roundToCurrency(billAmount)
  }

  const serviceDuration = differenceInDays(parsedServiceEndDate, parsedServiceStartDate) + 1
  const applicableServiceStart = max([parsedServiceStartDate, parsedOccupancyDate])
  const applicableServiceEnd = parsedFinalLiabilityDate
    ? min([parsedServiceEndDate, parsedFinalLiabilityDate])
    : parsedServiceEndDate
  const applicableDuration = differenceInDays(applicableServiceEnd, applicableServiceStart) + 1

  return roundToCurrency((billAmount * applicableDuration) / serviceDuration)
}

export const getDefaultAmountForBill = ({
  bill,
  finalLiabilityDate,
  occupancyDate,
  rentalId
}: {
  bill: UtilityBill
  finalLiabilityDate?: string | null
  occupancyDate: string
  rentalId: string
}) => {
  const existingChargeback = getChargebackForRental(bill, rentalId)

  if (existingChargeback?.finalized_amount != null) {
    return Number(existingChargeback.finalized_amount)
  }

  if (existingChargeback) {
    return getProratedChargebackAmount({
      billAmount: Number(bill.amount),
      finalLiabilityDate,
      occupancyDate,
      serviceEndDate: bill.service_end_date,
      serviceStartDate: bill.service_start_date
    })
  }

  return Number(bill.amount)
}

export const getErrorMessage = (error: unknown) => {
  if (typeof error === 'string' && error) {
    return error
  }

  if (error && typeof error === 'object') {
    const directMessage = 'message' in error ? error.message : undefined

    if (typeof directMessage === 'string' && directMessage) {
      return directMessage
    }

    const directError = 'error' in error ? error.error : undefined

    if (typeof directError === 'string' && directError) {
      return directError
    }
  }

  const message = (error as AxiosError<{ message?: string }>)?.response?.data?.message

  if (message) {
    return message
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'Unable to create utility chargeback'
}
